import { createApp } from "vue"
import { createPinia } from "pinia"
import { Capacitor } from "@capacitor/core"
import { IonicVue } from "@ionic/vue"
import maplibregl from "maplibre-gl"
import { Protocol } from "pmtiles"
import VueMaplibreGl from "@indoorequal/vue-maplibre-gl"

/* Core CSS required for Ionic components to work properly */
import "@ionic/vue/css/core.css"

/* Basic CSS for apps built with Ionic */
import "@ionic/vue/css/normalize.css"
import "@ionic/vue/css/structure.css"
import "@ionic/vue/css/typography.css"

/* Ionic dark mode follows the system setting. */
import "@ionic/vue/css/palettes/dark.system.css"

/* Project theme + MapLibre styles */
import "./theme/variables.css"
import "maplibre-gl/dist/maplibre-gl.css"

import App from "./App.vue"
import router from "./router/index.js"
import { i18n } from "./i18n/index.js"
import { initNavigator } from "./navigator.js"
import { DEFAULT_APP_CONFIG } from "./services/app.config.js"
import { useCapacitorPreferences } from "@infra/preferences/capacitor"
import { useBundledTilesSource } from "@infra/tiles/bundled"
import { useInMemoryTilesSource } from "@infra/tiles/inmemory"

// PMTiles protocol must be registered on the global maplibregl instance
// before any map loads a `pmtiles://` style. Register exactly once per
// page lifetime.
const protocol = new Protocol()
maplibregl.addProtocol("pmtiles", protocol.tile)

const platform = Capacitor.getPlatform() as "ios" | "android" | "web"

// Web: lazy Range fetch straight from the WebView origin. Native: Capacitor's
// asset server can't do Range, so read each tiny archive whole into RAM and
// serve ranges from memory via the same pmtiles protocol instance.
initNavigator({
  appConfig: DEFAULT_APP_CONFIG,
  preferences: useCapacitorPreferences(),
  tilesSource: platform === "web" ? useBundledTilesSource() : useInMemoryTilesSource(protocol),
  platform,
})

const app = createApp(App).use(createPinia()).use(IonicVue).use(i18n).use(router).use(VueMaplibreGl)

router.isReady().then(() => {
  app.mount("#app")
})
